import { Component, inject, ViewChild } from '@angular/core';
import { HeaderComponent } from "../header/header.component";
import { FooterComponent } from "../footer/footer.component";
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { ItIconComponent } from 'design-angular-kit';
import { CruscottoRichiestaService } from './cruscotto-richiesta.service';
import { Segnalazione } from '../types/Segnalazione';
import { Inviante } from '../types/Inviante';
import { Stato } from '../types/Stato';
import { CentraleCOT } from '../types/CentraleCOT';
import { forkJoin } from 'rxjs';
import { Richiesta } from '../types/Richiesta';
import { Router } from '@angular/router';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MAT_DATE_LOCALE, MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SicotService } from '../sicot.service';
import { DettaglioRichiestaService } from './dettaglio-richiesta/dettaglio-richiesta.service';
import { PopupConfermaArchiviazioneComponent } from './popup-conferma-archiviazione/popup-conferma-archiviazione.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { OperatoriCoinvoltiDialogComponent } from './operatori-coinvolti-dialog/operatori-coinvolti-dialog.component';

@Component({
  selector: 'app-cruscotto-richiesta',
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    ItIconComponent,
    NgIf,
    NgFor,
    MatTableModule,
    MatPaginatorModule,
    MatMenuModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule, FormsModule,
    MatCardModule,
    MatFormFieldModule, MatInputModule, MatOptionModule, MatSelectModule, MatDatepickerModule
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' }
  ],
  templateUrl: './cruscotto-richiesta.component.html',
  styleUrl: './cruscotto-richiesta.component.css'
})
export class CruscottoRichiestaComponent {

  API_ENDPOINT_COT_SERVICE = this.sicotService.API_ENDPOINT_COT_SERVICE;

  loading = false;

  formRicerca = this.fb.group({
    inviante: [[] as string[]],
    dataArrivoDa: [''],
    dataArrivoA: [''],
    segnalazione: [[] as number[]],
    centrale: [[] as number[]],
    stato: [''],
    ricerca: ['']
  });

  segnalazioni: Segnalazione[] = [];
  invianti: Inviante[] = [];
  stati: Stato[] = [];
  centraliCOT: CentraleCOT[] = [];
  richieste: any;
  richiesteInLavorazione: number = 0;

  pageSize = 20;
  pageElements = 0;
  pageIndex = 0;

  displayedColumns: string[] = [
    'apri',
    'id',
    'cognome',
    'nome',
    'codiceFiscale',
    'segnalazione',
    'stato',
    'settingProposto',
    'settingDefinitivo',
    'dataArrivo',
    'siInviate',
    'struttureInviate',
    'centraleCOT',
    'strutturaRicevente',
    'alert',
    'operatore',
    'actions'
  ];

  @ViewChild(MatSort) sort: MatSort | undefined;

  ngAfterViewInit() { }

  private readonly dialog = inject(MatDialog);

  constructor(
    private richiesteService: CruscottoRichiestaService,
    private router: Router,
    private fb: FormBuilder,
    private http: HttpClient,
    private sicotService: SicotService,
    private dettaglioRichService: DettaglioRichiestaService
  ) { }

  openOperatoriCoinvolti(richiesta: Richiesta): void {
    this.dialog.open(OperatoriCoinvoltiDialogComponent, {
      data: { richiesta },
      width: '400px',
    });
  }

  apriRichiesta(id: number) {
    this.router.navigate([`/richiesta/${id}`]);
  }

  ngOnInit() {
    this.loading = true;

    // Tipi segnalazione: SEMPRE tutti visibili nel filtro. La tabella ponte user_tipo_segnalazione
    // influenza solo il precaricamento (valori pre-selezionati) dall'area personale dell'utente loggato.
    // Centrali COT: in tendina solo quelle abilitate all'utenza; le richieste sono filtrate solo per quelle centrali.
    this.sicotService.getTipiSegnalazioneAdmin().subscribe({
      next: (data) => {
        this.segnalazioni = [...(data ?? [])]
          .map(t => ({ id: t.id, codice: t.codice, descrizione: t.descrizione } as Segnalazione))
          .sort((a, b) => (a.descrizione ?? '').localeCompare(b.descrizione ?? '', 'it'));
        const userId = this.sicotService.getUserId();
        if (userId) {
          forkJoin({
            userTipi: this.sicotService.getUserTipiSegnalazione(userId),
            userCentrali: this.sicotService.getUserCots(userId),
          }).subscribe({
            next: ({ userTipi, userCentrali }) => {
              const defaultSegnalazioneIds = (userTipi ?? []).map(t => t.id).filter((id): id is number => id != null);
              if (defaultSegnalazioneIds.length) {
                this.formRicerca.patchValue({ segnalazione: defaultSegnalazioneIds });
              }
              const cots = userCentrali ?? [];
              this.centraliCOT = cots
                .map(c => ({ id: c.id, codice: c.codice, denominazione: c.descrizione ?? c.codice } as CentraleCOT))
                .sort((a, b) => (a.codice ?? '').localeCompare(b.codice ?? '', 'it'));
              const defaultCentraliIds = cots.map(c => c.id).filter((id): id is number => id != null);
              this.formRicerca.patchValue({ centrale: defaultCentraliIds });
              this.getRichieste();
            },
            error: () => {
              this.centraliCOT = [];
              this.getRichieste();
            },
          });
        } else {
          this.centraliCOT = [];
          this.getRichieste();
        }
      },
      error: () => {
        this.segnalazioni = [];
        this.centraliCOT = [];
        this.getRichieste();
      },
    });

    this.richiesteService.getSistemiInformativi()
      .subscribe((data: Inviante[]) => {
        this.invianti = [...data].sort((a, b) => (a.descrizione ?? '').localeCompare(b.descrizione ?? '', 'it'));
      });

    this.richiesteService.getStati()
      .subscribe((data: Stato[]) => {
        this.stati = [...data].sort((a, b) => (a.descrizione ?? '').localeCompare(b.descrizione ?? '', 'it'));
      });
  }

  getRichieste() {
    const invianti = this.formRicerca.value.inviante ?? [];
    const segnalazioni = this.formRicerca.value.segnalazione ?? [];
    const centrali = this.formRicerca.value.centrale ?? [];
    const stato = this.formRicerca.value.stato;
    const ricerca = this.formRicerca.value.ricerca;

    let dataArrivoDa: string | null = null;
    let dataArrivoA: string | null = null;
    const da = this.formRicerca.value.dataArrivoDa;
    const a = this.formRicerca.value.dataArrivoA;
    if (da) {
      const d = new Date(da);
      dataArrivoDa = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    }
    if (a) {
      const d = new Date(a);
      dataArrivoA = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    }

    this.richiesteService.getRichieste(invianti, dataArrivoDa, dataArrivoA, segnalazioni, centrali, stato, ricerca, this.pageSize)
      .subscribe((data: any) => {

        this.pageElements = data.totalElements;
        this.richieste = new MatTableDataSource<Richiesta[]>(data.content);
        this.richieste.sort = this.sort;
        this.richiesteInLavorazione = data.totalElementsInLav;

        this.richieste.sortingDataAccessor = (item: any, property: any) => {
          switch (property) {
            case 'cognome': return item.assistito?.cognome;
            case 'nome': return item.assistito?.nome;
            case 'codiceFiscale': return item.assistito?.codiceFiscale;
            case 'dataArrivo': return item.dataDimissionePrevista ? new Date(item.dataDimissionePrevista).getTime() : 0;
            default: return item[property];
          }
        };
        this.loading = false;
      })
  }

  onSubmit() {
    this.getRichieste();
  }

  pulisciForm() {
    this.formRicerca.reset({
      inviante: [],
      dataArrivoDa: '',
      dataArrivoA: '',
      segnalazione: [],
      centrale: [],
      stato: '',
      ricerca: ''
    });
    this.getRichieste();
  }


  exportExcel() { }

  nuovaRichiesta() {


    this.http.get(this.API_ENDPOINT_COT_SERVICE + '/assets/richiesta-valutazione-assistito.json').subscribe(
      (data) => {
        console.log('JSON Data Loaded:', data);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body: any = data;
        body.richiestaId = Math.floor(Math.random() * 9000) + 1000;
        this.http.post<any>(
          this.API_ENDPOINT_COT_SERVICE + '/richiesta-valutazione-assistito',
          body, { headers }
        ).subscribe(
          (data) => {
            console.log(data);
          },
          (error) => {
            console.log(error);
          }
        );
      },
      (error) => {
        console.error('Error loading JSON:', error);
      }
    );
  }

  archiviaRichiesta(richiesta: Richiesta) {

    let id = richiesta.id;
    if (id) {
      this.dettaglioRichService.archiviaRichiesta(id.toString())
        .subscribe({
          next: (response: any) => {
            this.getRichieste();
            this.popupConfermaArchiviazione(richiesta);
          },
          error: (error: any) => {
            console.error('Errore durante l\'esecuzione delle operazioni:', error);
          }
        });
    }

  }

  private _snackBar = inject(MatSnackBar);
  durationInSeconds = 5;

  popupConfermaArchiviazione(richiesta: Richiesta) {
    this._snackBar.openFromComponent(PopupConfermaArchiviazioneComponent, {
      data: richiesta,
      duration: this.durationInSeconds * 1000,
    });
  }

  handlePageEvent(e: any) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.pageElements = e.length;
  }

}